import * as React from "react";
import { styled } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import MuiDrawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Container from "@mui/material/Container";
import MenuIcon from "@mui/icons-material/Menu";
import LogoTP from "../../assets/images/LogoTPBlanco.svg";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { MainListItems, secondaryListItems } from "./listItems";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { Users } from "../users/users";
import AccountMenu from "./AccountMenu";
import { useSelector } from "react-redux";
import { selectUser } from "../../features/user/userSlice";
import { Events } from "../events/Events";
import { Event } from "../events/Event";
import { Statistics } from "../statistics/Statistics";
import { Sensores } from "../sensores/Sensores";
import { StatisticsAnalists } from "../statistics/StatisticsAnalists";
import { EventsReport } from "../reports/EventsReport";
import Calendar from "../calendar/Calendar";
import { UsersCalendar } from "../calendar/users";
import { CalendarSpecialists } from "../calendar/CalendarSpecialists";
import { Messages } from "../calendar/Messages";
import { Monitoreo } from "../monitoreo/Monitoreo";
import { useGetNodosAlertasQuery } from "../../features/api/apiYeapGo";
import { useSnackbar } from "notistack";
import useInterval from "use-interval";
import { Mails } from "../monitoreo/Mails";
import { NodesReports } from "../monitoreo/NodesReports";
import { MonitoreoHistorico } from "../monitoreo/MonitoreoHistorico";

const drawerWidth = 240;

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  "& .MuiDrawer-paper": {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: "border-box",
    ...(!open && {
      overflowX: "hidden",
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      width: theme.spacing(7),
      [theme.breakpoints.up("sm")]: {
        width: theme.spacing(9),
      },
    }),
  },
}));

function DashboardContent() {
  const user = useSelector(selectUser);
  const [open, setOpen] = React.useState(true);
  const { enqueueSnackbar } = useSnackbar();
  const [show, setShow] = React.useState(false);
  let navigate = useNavigate();
  const toggleDrawer = () => {
    setOpen(!open);
  };
  const { data: nodos, isLoading } = useGetNodosAlertasQuery(
    { Token: user.Token },
    {
      pollingInterval: 1000 * 10,
    }
  );

  const showAlerts = () => {
    if (nodos && user?.Rol === "Administrador") {
      let agregadosMensaje = "Se han agregado ";
      let desagregadosMensaje = "Se han retirado ";
      agregadosMensaje +=
        nodos.volumenes.Agregados > 0
          ? `| ${nodos.volumenes.Agregados} volúmenes|`
          : "";

      desagregadosMensaje +=
        nodos.volumenes.Desagregados > 0
          ? `| ${nodos.volumenes.Desagregados} volúmenes|`
          : "";

      agregadosMensaje +=
        nodos.interfaces.Agregados > 0
          ? `| ${nodos.interfaces.Agregados} interfaces|`
          : "";

      desagregadosMensaje +=
        nodos.interfaces.Desagregados > 0
          ? `| ${nodos.interfaces.Desagregados} interfaces|`
          : "";

      agregadosMensaje +=
        nodos.nodos.Agregados > 0 ? `| ${nodos.nodos.Agregados} nodos|` : "";

      desagregadosMensaje +=
        nodos.nodos.Desagregados > 0
          ? `| ${nodos.nodos.Desagregados} nodos|`
          : "";

      nodos.volumenes.Agregados +
        nodos.interfaces.Agregados +
        nodos.nodos.Agregados >
        0 &&
        enqueueSnackbar(agregadosMensaje, {
          variant: "success",
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          onClick: () => {
            navigate("/nodos");
          },
        });

      nodos.volumenes.Desagregados +
        nodos.interfaces.Desagregados +
        nodos.nodos.Desagregados >
        0 &&
        enqueueSnackbar(desagregadosMensaje, {
          variant: "error",
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          onClick: () => {
            navigate("/nodos");
          },
        });
    }
  };
  React.useEffect(() => {
    if (nodos && !show) {
      showAlerts();
      setShow(true);
    }
  }, [nodos, show]);
  useInterval(() => {
    showAlerts();
  }, 1000 * 60 * 10);
  React.useEffect(() => {
    if (!user.Token) {
      navigate("login");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar position="absolute" open={open} color="primary">
        <Toolbar
          sx={{
            pr: "24px", // keep right padding when drawer closed
          }}
        >
          <IconButton
            edge="start"
            color="inherit"
            aria-label="open drawer"
            onClick={toggleDrawer}
            sx={{
              marginRight: "36px",
              ...(open && { display: "none" }),
            }}
          >
            <MenuIcon />
          </IconButton>
          <img src={LogoTP} alt={"Teleperformance"} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              component="span"
              variant="h6"
              noWrap
              color="inherit"
              sx={{ fontWeight: "bold" }}
            >
              Yeap
            </Typography>
            <Typography
              component="span"
              variant="h6"
              noWrap
              color="secondary"
              sx={{ fontWeight: "bold" }}
            >
              Go!
            </Typography>
          </Box>

          <AccountMenu />
        </Toolbar>
      </AppBar>
      <Drawer variant="permanent" open={open}>
        <Toolbar
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            px: [1],
          }}
        >
          <IconButton onClick={toggleDrawer}>
            <ChevronLeftIcon />
          </IconButton>
        </Toolbar>
        <Divider />
        <List component="nav" color="secondary">
          <MainListItems />
          <Divider sx={{ my: 1 }} />
          {secondaryListItems}
        </List>
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          height: "100vh",
          overflow: "auto",
        }}
      >
        <Toolbar />
        <Container maxWidth={false} sx={{ mt: 4, mb: 4, ml: 1, mr: 1 }}>
          <Routes>
            <Route path="nodos" element={<Monitoreo />}></Route>
            <Route path="nodos/reports" element={<NodesReports />}></Route>
            <Route path="nodos/historial" element={<MonitoreoHistorico />}></Route>

            <Route path="eventos/:id" element={<Event />}></Route>
            <Route path="usuarios" element={<Users />}></Route>
            <Route path="eventos" element={<Events />}></Route>
            <Route
              path="estadisticas/Analistas"
              element={<StatisticsAnalists />}
            ></Route>
            <Route
              path="especialistasturno"
              element={<CalendarSpecialists />}
            ></Route>
            <Route path="mensajes" element={<Messages />}></Route>
            <Route path="mails" element={<Mails />}></Route>
            <Route path="usuariosturno" element={<UsersCalendar />}></Route>
            <Route path="turnos" element={<Calendar />}></Route>
            <Route path="reporte" element={<EventsReport />}></Route>
            <Route path="estadisticas" element={<Statistics />}></Route>
            <Route path="sensores" element={<Sensores />}></Route>
            <Route
              path="/*"
              element={<Navigate to="eventos" replace />}
            ></Route>
          </Routes>
        </Container>
      </Box>
    </Box>
  );
}

export default function Dashboard() {
  return <DashboardContent />;
}
